import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { Flag } from "@/components/Flag";

const LANGS = ["fr", "en", "es", "pt"];
const EMPTY = { fr: "", en: "", es: "", pt: "" };

const LabelInputs = ({ value, onChange, prefix }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
    {LANGS.map((l) => (
      <div key={l} className="flex items-center gap-1.5">
        <Flag code={l} size={14} />
        <Input value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} placeholder={l.toUpperCase()} data-testid={`${prefix}-${l}`} />
      </div>
    ))}
  </div>
);

export const InterestsPanel = ({ canManage = true }) => {
  const [items, setItems] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [algorithms, setAlgorithms] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabels, setNewLabels] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editLabels, setEditLabels] = useState(EMPTY);

  const load = () => api.get("/admin/interests").then(({ data }) => setItems(data.interests || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/settings").then(({ data }) => {
      const a = data.settings?.algorithms || {};
      setAlgorithms(a);
      setMinScore(Number(a.matching?.min_score) || 0);
    }).catch(() => {});
  }, []);

  const saveThreshold = async () => {
    try {
      const v = Math.max(0, Math.min(100, Number(minScore) || 0));
      await api.patch("/admin/settings", { algorithms: { ...(algorithms || {}), matching: { ...((algorithms || {}).matching || {}), min_score: v } } });
      setMinScore(v);
      toast.success("Seuil enregistré");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const add = async () => {
    try {
      await api.post("/admin/interests", { labels: newLabels });
      toast.success("Point commun ajouté");
      setNewLabels(EMPTY);
      setAdding(false);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const startEdit = (it) => { setEditId(it.id); setEditLabels({ ...EMPTY, ...(it.labels || {}) }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/interests/${editId}`, { labels: editLabels });
      toast.success("Modifié");
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.labels?.fr || it.key} » ? Il sera retiré du profil de ${it.member_count} membre(s).`)) return;
    try {
      await api.delete(`/admin/interests/${it.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-interests-panel">
      <div className="yalola-card p-5">
        <div className="font-bold font-display text-lg mb-1">Seuil de compatibilité minimum</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          En dessous de ce score, aucun profil n'est proposé dans les « Points en commun ». Mets 0 pour tout afficher.
        </p>
        <div className="flex items-center gap-2">
          <Input type="number" min="0" max="100" value={minScore} onChange={(e) => setMinScore(e.target.value)} className="max-w-[110px]" data-testid="interests-min-score" />
          <span className="text-sm font-semibold" style={{ color: "var(--yalola-primary)" }}>%</span>
          <Button className="pill-primary rounded-full" onClick={saveThreshold} disabled={!canManage} data-testid="interests-min-score-save">Enregistrer</Button>
        </div>
      </div>

      <div className="yalola-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div>
            <div className="font-bold font-display text-lg">Centres d'intérêt</div>
            <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{items.length} points en commun · libellés dans les 4 langues.</p>
          </div>
          {canManage && !adding && (
            <Button className="pill-primary rounded-full" onClick={() => setAdding(true)} data-testid="interest-add-btn"><Plus size={15} className="mr-1" /> Ajouter un point commun</Button>
          )}
        </div>

        {adding && (
          <div className="rounded-xl p-4 my-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="interest-add-form">
            <LabelInputs value={newLabels} onChange={setNewLabels} prefix="interest-new" />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setAdding(false); setNewLabels(EMPTY); }} data-testid="interest-add-cancel"><X size={14} className="mr-1" /> Annuler</Button>
              <Button size="sm" className="pill-primary rounded-full" onClick={add} data-testid="interest-add-save"><Check size={14} className="mr-1" /> Ajouter</Button>
            </div>
          </div>
        )}

        <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
          {items.map((it) => (
            <div key={it.id} className="py-3" data-testid={`interest-row-${it.key}`}>
              {editId === it.id ? (
                <div className="space-y-2">
                  <LabelInputs value={editLabels} onChange={setEditLabels} prefix={`interest-edit-${it.key}`} />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditId(null)} data-testid={`interest-edit-cancel-${it.key}`}><X size={14} /></Button>
                    <Button size="sm" className="pill-primary rounded-full" onClick={saveEdit} data-testid={`interest-edit-save-${it.key}`}><Check size={14} className="mr-1" /> Enregistrer</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{it.labels?.fr || it.key}</div>
                    <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>
                      {LANGS.slice(1).map((l) => it.labels?.[l]).filter(Boolean).join(" · ")} <span className="opacity-60">({it.key})</span>
                    </div>
                  </div>
                  <div className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{it.member_count} membre{it.member_count > 1 ? "s" : ""}</div>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      <button type="button" className="p-1.5 rounded-lg hover:bg-[rgba(0,0,0,0.05)]" onClick={() => startEdit(it)} data-testid={`interest-edit-${it.key}`}><Pencil size={14} /></button>
                      <button type="button" className="p-1.5 rounded-lg hover:bg-[rgba(0,0,0,0.05)]" style={{ color: "#DC2626" }} onClick={() => remove(it)} data-testid={`interest-del-${it.key}`}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="py-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucun point commun.</div>}
        </div>
      </div>
    </div>
  );
};
